import RoundButton from 'Components/RoundButton';
import TipContent from 'Components/Tooltip';
import { exitFullMode } from 'Components/VideoControls/helper';
import { Box, Button, Drop, Tip } from 'grommet';
import { Camera, LoaderCircle } from 'lucide-preact';
import { useContext, useEffect, useRef, useState } from 'preact/hooks';
import { connect, useDispatch } from 'react-redux';
import { GlobalsContext } from 'Shared/context';
import { logGAEvent } from 'Shared/ga';

import { useIsMobileSize, usePendingUGC } from '@/hooks';
import { setIsCreatingUGC, setSnapshotData } from '@/redux/actions';

import { getDesktopOrMobile } from '../../../../../helpers';
import { showTakeSnapshotModal } from '../../../../../routes/habitat/components/TakeSnapshotModal/actions';
import { capturePhotoCreditCheckMessage } from '../../actions';
import style from './style.scss';

let timeout;

const TakeSnapshotButton = ({
  plain,
  cameraId,
  userId,
  dailyCredits,
  fullScreen,
  slug,
  streamKey,
  habitatId,
  setSnapshotDataAction,
  showTakeSnapshotModalAction,
}) => {
  const { socket } = useContext(GlobalsContext);
  const [loading, setLoading] = useState();
  const [tryAgain, setTryAgain] = useState();
  const isMobileSize = useIsMobileSize();
  const device = getDesktopOrMobile();
  const dispatch = useDispatch();

  const isPendingUGC = usePendingUGC();

  const ref = useRef();

  const clickHandler = () => {
    if (isPendingUGC) {
      return;
    }

    if (dailyCredits?.inUse && dailyCredits.capturePhoto === 0) {
      capturePhotoCreditCheckMessage(dailyCredits, dispatch);
      return;
    }
    logGAEvent('snapshot-button', 'click', slug);
    socket.emit('userTookSnapshot', { cameraId, userId });
    setSnapshotDataAction({ loading: true });
    setLoading(true);
    dispatch(setIsCreatingUGC(true));

    // This is a fallback in case socket didn't return any photo in 8 seconds
    timeout = setTimeout(() => {
      setLoading(false);
      dispatch(setIsCreatingUGC(false));
      setTryAgain(true);
    }, 8000);
  };

  useEffect(() => {
    const socketHandler = (data = {}) => {
      if (data?.userIds?.includes(userId) || data?.userId === userId) {
        logGAEvent('snapshot-button', 'photo-received', slug);
        setLoading(false);
        dispatch(setIsCreatingUGC(false));
        setTryAgain(false);
        clearTimeout(timeout);
        timeout = undefined;
        showTakeSnapshotModalAction();
        setSnapshotDataAction({ ...data, loading: false });
      }

      if (fullScreen) {
        exitFullMode();
      }
    };

    if (socket) {
      socket.on('snapshotTaken', socketHandler);
    }

    return () => {
      if (socket) {
        socket.off('snapshotTaken', socketHandler);
      }
    };
  }, [fullScreen, isMobileSize, socket, userId, setSnapshotDataAction, dispatch]);

  if (plain) {
    return (
      <Button ref={ref} plain onClick={clickHandler} disabled={loading || isPendingUGC} className={style.mobileButton}>
        {loading ? <LoaderCircle className="animate-spin" color="#fff" size={24} /> : <Camera color="#fff" size={24} />}
        {ref.current && tryAgain && (
          <Drop
            background="var(--red)"
            margin={{ bottom: '25px' }}
            align={{ bottom: 'top', left: 'left' }}
            target={ref.current}
            round="7px"
            onClickOutside={() => setTryAgain(false)}
          >
            <div className={style.tryAgain}>Oops, try again!</div>
          </Drop>
        )}
      </Button>
    );
  }

  return (
    <div ref={ref}>
      {ref.current && tryAgain && (
        <Drop
          background="var(--red)"
          margin={{ left: '10px' }}
          align={{ left: 'right' }}
          target={ref.current}
          round="7px"
          onClickOutside={() => setTryAgain(false)}
        >
          <div className={style.tryAgain}>Oops, try again!</div>
        </Drop>
      )}
      {device === 'mobile' ? (
        <Box>
          <RoundButton
            onClick={clickHandler}
            width="36"
            backgroundColor="var(--secondaryGreen)"
            color="white"
            disabled={loading || isPendingUGC}
          >
            <div className={style.innerContainerSnapshotButton}>
              {loading ? <LoaderCircle className="animate-spin" color="white" size={24} /> : <Camera size={24} />}
            </div>
          </RoundButton>
        </Box>
      ) : (
        <Tip dropProps={{ align: { left: 'right' } }} content={<TipContent message="Take a Photo" />} plain>
          <Box>
            <RoundButton
              onClick={clickHandler}
              width="36"
              backgroundColor="var(--secondaryGreen)"
              color="white"
              disabled={loading || isPendingUGC}
            >
              <div className={style.innerContainerSnapshotButton}>
                {loading ? <LoaderCircle className="animate-spin" color="white" size={24} /> : <Camera size={24} />}
              </div>
            </RoundButton>
          </Box>
        </Tip>
      )}
    </div>
  );
};

export default connect(
  ({
    user: { userId, dailyCredits },
    habitat: {
      habitatInfo: {
        _id: habitatId,
        selectedCamera: { _id: cameraId, streamKey } = {},
        slug: habitatSlug,
        zoo: { slug: zooSlug } = {},
      },
    },
    mainStream: { fullScreen },
  }) => ({
    userId,
    dailyCredits,
    cameraId,
    fullScreen,
    streamKey,
    habitatId,
    slug: `${zooSlug}/${habitatSlug}`,
  }),
  {
    setSnapshotDataAction: setSnapshotData,
    showTakeSnapshotModalAction: showTakeSnapshotModal,
  },
)(TakeSnapshotButton);
